import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Point } from '../types';

interface AddPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (point: Point) => void;
  initialData: Partial<Point> | null;
  onLocationSelect?: (location: [number, number] | null) => void;
}

export function AddPointModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  onLocationSelect,
}: AddPointModalProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<[number, number] | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      const ymaps = (window as any).ymaps;

      setName(initialData?.name || '');
      setAddress(initialData?.address || '');
      setSelectedLocation(initialData?.location || null);

      // nameInputRef.current?.focus();
      if (initialData?.location && !initialData.address) {
        // Creating a new point with a prefilled location.
        const prefillLocation = initialData.location!;

        if (ymaps) {
          ymaps.geocode(prefillLocation).then((result: any) => {
            const firstGeoObject = result.geoObjects.get(0);
            const humanReadableAddress = firstGeoObject.getAddressLine() || prefillLocation.join(', ');
            setAddress(humanReadableAddress);
          }).catch((err: any) => {
            console.error('Geocoding error:', err);
            setAddress(prefillLocation.join(', '));
          });
        } else {
          setAddress(prefillLocation.join(', '));
        }
      }
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const ymaps = (window as any).ymaps;
    if (!locationInputRef.current || !ymaps) return;

    let suggestView: any = null;

    ymaps.ready(() => {
      if (!locationInputRef.current) {
        return;
      }
      suggestView = new ymaps.SuggestView(locationInputRef.current);
      suggestView.events.add('select', (e: any) => {
        const selectedItem = e.get('item');
        geocodeAddress(selectedItem.value);
      });
    });

    return () => {
      if (suggestView) {
        suggestView.destroy();
      }
    };
  }, [isOpen]);

  // Add geocoding function
  const geocodeAddress = (value: string) => {
    (window as any).ymaps.geocode(value).then((result: any) => {
      const firstGeoObject = result.geoObjects.get(0);
      const coords = firstGeoObject.geometry.getCoordinates();
      setAddress(value);
      setSelectedLocation(coords);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      geocodeAddress(address);
    }
  };

  useEffect(() => {
    onLocationSelect?.(selectedLocation);
  }, [selectedLocation, onLocationSelect]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;

    onSubmit({
      ...(initialData || {}), // Preserve existing point data
      name,
      address,
      location: selectedLocation,
    });
    onClose();
  };

  const handleClose = () => {
    setSelectedLocation(null);
    onLocationSelect?.(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* <motion.div
            className="fixed inset-0 bg-black/10 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          /> */}
          <motion.div
            className="fixed left-0 right-0 top-0 bg-white p-4 z-50 max-w-lg mx-auto rounded-b-2xl"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-surface-800 flex-1">
                  {initialData?.id ? 'Edit Point' : 'Add New Point'}
                </h3>
                <button
                  type="submit"
                  className="bg-brand-500 text-white px-4 py-2 mx-2 rounded-lg hover:bg-brand-600 transition-colors"
                  disabled={!selectedLocation}
                >
                  {initialData?.id ? 'Save Changes' : 'Add Point'}
                </button>

                <button className="text-surface-800 px-2 py-2 rounded-lg hover:bg-surface-300 transition-colors" onClick={handleClose}>
                  <X className="w-5 h-5 text-surface-400" />
                </button>
              </div>

              <input
                ref={nameInputRef}
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Enter point name..."
                required
              />

              <div className="relative">
                <input
                  ref={locationInputRef}
                  type="text"
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-10 pr-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Enter location..."
                  required
                />
                <Search className="w-5 h-5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}